import { useState, useRef, useEffect } from 'react';
import { HiAdjustmentsHorizontal } from "react-icons/hi2";
import { createPortal } from 'react-dom';

interface ColumnVisibilityProps {
  columns: Array<{
    key: string;
    label: string;
  }>;
  visibleColumns: Record<string, boolean>;
  onColumnToggle: (key: string) => void;
  projectId?: string;
}

const ColumnVisibility: React.FC<ColumnVisibilityProps> = ({
  columns,
  visibleColumns,
  onColumnToggle,
  projectId
}) => {
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const columnDropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  // Update dropdown position when it's shown
  useEffect(() => {
    if (showColumnDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 5,
        right: window.innerWidth - rect.right - window.scrollX
      });
    }
  }, [showColumnDropdown]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target as Node) && 
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowColumnDropdown(false);
      }
    }
    if (showColumnDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnDropdown]);

  const handleColumnToggle = (key: string) => {
    // Count how many columns are currently visible
    const visibleCount = Object.values(visibleColumns).filter(Boolean).length;
    
    // If trying to uncheck the last visible column, show tooltip and prevent action
    if (visibleCount === 1 && visibleColumns[key]) {
      setShowTooltip(key);
      setTimeout(() => setShowTooltip(null), 2000);
      return;
    }
    
    onColumnToggle(key);
  };

  return (
    <div className="absolute right-4 top-1/2 -translate-y-1/2 z-[500]">
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex items-center justify-center p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
        onClick={() => setShowColumnDropdown(v => !v)}
        title="Edit Columns"
      >
        <HiAdjustmentsHorizontal className="w-4 h-4" />
      </button>
      
      {showColumnDropdown && createPortal(
        <div
          ref={columnDropdownRef}
          className="fixed z-[1000] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            width: '192px'
          }}
        >
          <div className="p-2">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Visible Columns</div>
            <div className="overflow-y-auto pr-1" style={{ maxHeight: '320px' }}>
            {columns.map(col => (
              <div key={col.key} className="relative">
                <div className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <input
                    type="checkbox"
                    id={`col-${col.key}`}
                    checked={visibleColumns[col.key]}
                    onChange={() => handleColumnToggle(col.key)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor={`col-${col.key}`} className="font-medium text-sm text-gray-700 dark:text-gray-300">
                    {col.label}
                  </label>
                </div>
                {showTooltip === col.key && (
                  <div className="absolute left-0 top-full mt-1 px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 whitespace-nowrap z-50 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span>Keep at least one column</span>
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColumnVisibility; 